import { useEffect, useState } from 'react';
import api from '../api';
import { Button, Wordmark } from '../components';
import { LegalModal, type LegalDoc } from './LegalModal';
import { useAuth } from '../state';

/**
 * One-time consent dialog shown to a user before their first
 * "sensitive" interaction with EntreDash — first idea capture, first
 * strategy session message, etc. Sets a server-side timestamp on the
 * user record so the dialog never appears for the same account again.
 *
 * Why this exists:
 *  - Tester research showed "could my idea be stolen?" was the #1
 *    blocker to engagement. A plain-English consent moment establishes
 *    trust before the user shares anything sensitive.
 *  - The patent attorney advised that an acknowledged Confidentiality
 *    Terms record materially strengthens the position that use of
 *    EntreDash does not constitute "public disclosure" under §102(b).
 *
 * Usage pattern in calling pages:
 *
 *   const profile = useAuth((s) => s.profile);
 *   const [showConfidentiality, setShowConfidentiality] = useState(false);
 *
 *   const handleSubmit = async () => {
 *     if (profile && !profile.confidentialityAcknowledgedAt) {
 *       setShowConfidentiality(true);
 *       return; // bail; modal will re-trigger submit on accept
 *     }
 *     // ...proceed with real submission
 *   };
 *
 *   <ConfidentialityModal
 *     open={showConfidentiality}
 *     onAccept={() => { setShowConfidentiality(false); handleSubmit(); }}
 *     onClose={() => setShowConfidentiality(false)}
 *   />
 */
/** LocalStorage key for anonymous (pre-signup) acknowledgment. Once
 *  set, the modal won't re-trigger for anonymous visitors on this
 *  browser. After signup, the user-level `confidentialityAcknowledgedAt`
 *  field takes precedence — this localStorage flag is just a UX hint
 *  to suppress the modal for visitors who already accepted. */
export const ANON_ACK_KEY = 'meinvent_confidentiality_ack_at';

/** Read the anonymous acknowledgment timestamp from localStorage.
 *  Returns 0 (falsy) when not yet acknowledged or when localStorage
 *  is unavailable (SSR, sandboxed iframe, etc.). */
export function readAnonymousConfidentialityAck(): number {
  if (typeof window === 'undefined') return 0;
  try {
    const raw = localStorage.getItem(ANON_ACK_KEY);
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

/** True when the visitor needs to see the modal — i.e., neither the
 *  signed-in user record nor the anonymous localStorage flag shows a
 *  prior acknowledgment. Use this at every "sensitive action" gate. */
export function needsConfidentialityAck(profile: {
  confidentialityAcknowledgedAt?: number;
} | null | undefined): boolean {
  if (profile && profile.confidentialityAcknowledgedAt) return false;
  if (readAnonymousConfidentialityAck() > 0) return false;
  return true;
}

export function ConfidentialityModal({
  open,
  onAccept,
  onClose,
  /**
   * When true, the modal runs in anonymous mode: it doesn't call the
   * authenticated `acknowledgeConfidentiality` backend method, and
   * stores the acceptance timestamp in localStorage instead. Use for
   * the landing page's "Test my idea" gate, where the user hasn't
   * signed up yet. Defaults to false — authenticated callers retain
   * the original "write to user record" behavior.
   */
  anonymous = false,
}: {
  open: boolean;
  onAccept: () => void;
  onClose: () => void;
  anonymous?: boolean;
}) {
  const setProfile = useAuth((s) => s.setProfile);
  const profile = useAuth((s) => s.profile);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Which legal document is open on top of this dialog, if any. */
  const [legalDoc, setLegalDoc] = useState<LegalDoc | null>(null);

  // While this is open, flag the page so the cookie banner holds back on phones (global.css) and can't cover
  // the "I understand" button. The banner appears once the modal closes.
  useEffect(() => {
    if (!open) return;
    document.body.dataset.confOpen = '1';
    return () => {
      delete document.body.dataset.confOpen;
    };
  }, [open]);

  if (!open) return null;

  const handleAccept = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (anonymous) {
        // Anonymous mode: write a timestamp to localStorage so the
        // modal doesn't re-trigger on subsequent /try visits or
        // mid-flow refreshes. No backend call — the user record
        // doesn't exist yet.
        try {
          localStorage.setItem(ANON_ACK_KEY, String(Date.now()));
        } catch {
          // localStorage may be unavailable; we still want to let the
          // user proceed. The modal will just appear again next time.
        }
        onAccept();
        return;
      }
      const { acknowledgedAt } = await api.acknowledgeConfidentiality();
      // Optimistically update the in-memory profile so the modal
      // doesn't re-trigger on the next render before the next getMe
      // refresh resolves.
      if (profile) {
        setProfile({ ...profile, confidentialityAcknowledgedAt: acknowledgedAt });
      }
      onAccept();
    } catch (e: any) {
      setError(e?.message ?? 'Could not save your acknowledgment. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confidentiality-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 20, 0.55)',
        backdropFilter: 'blur(4px)',
        zIndex: 220,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        style={{
          background: 'var(--surface-page)',
          borderRadius: 16,
          width: '100%',
          maxWidth: 560,
          padding: '32px 32px 28px',
          boxShadow: '0 12px 60px rgba(0,0,0,0.18)',
          animation: 'confidentialityDlg 220ms cubic-bezier(0.4, 0, 0.2, 1)',
          // Never taller than the viewport (minus the backdrop's 24px padding
          // top+bottom); scroll the dialog's own content when it doesn't fit.
          // Without this, on a short mobile screen the centered dialog overflows
          // off the top AND bottom with no way to scroll — so the accept button
          // is unreachable and a visitor can't consent to continue. dvh tracks
          // the real (chrome-aware) mobile viewport.
          maxHeight: 'calc(100dvh - 48px)',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div
          className="f-mono"
          style={{
            fontSize: 11,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: 'var(--brass-deep)',
            marginBottom: 10,
          }}
        >
          Before you share your idea
        </div>
        <h2
          id="confidentiality-title"
          style={{
            fontFamily: 'var(--f-serif)',
            fontWeight: 500,
            fontSize: 26,
            margin: '0 0 16px',
            lineHeight: 1.2,
          }}
        >
          Your idea stays yours.
        </h2>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.65,
            color: 'var(--ink)',
            margin: '0 0 20px',
          }}
        >
          <Wordmark size="inherit" /> is built so you can share your idea, and
          everything you build from it, safely. A few commitments we make to you:
        </p>
        <ul
          style={{
            fontSize: 14,
            lineHeight: 1.7,
            color: 'var(--ink)',
            margin: '0 0 22px',
            paddingLeft: 20,
          }}
        >
          <li>
            <strong>Confidential.</strong> Everything you enter is encrypted and
            stored on servers in the United States (AWS). We never sell it and
            never publish it.
          </li>
          <li>
            <strong>Looked up, not handed over.</strong> To check your idea
            against what&apos;s already out there, we send search terms drawn
            from it, not the idea itself, to patent, research, and web-search
            services, then read what comes back. Your idea is never posted,
            filed, or shared.
          </li>
          <li>
            <strong>Not used to train AI.</strong> Your ideas, conversations,
            and work are not used to train any AI model, confirmed by our
            infrastructure provider&apos;s{' '}
            <a
              href="https://trust.mindstudio.ai/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--brass-deep)' }}
            >
              SOC 2 Type II
            </a>{' '}
            controls.
          </li>
          <li>
            <strong>Under a confidentiality agreement.</strong> Using{' '}
            <Wordmark size="inherit" /> puts a mutual confidentiality agreement
            in force between you and us, written into our Terms of Service.
          </li>
          <li>
            <strong>Deletable at any time.</strong> Every idea, every memo,
            every conversation: gone with one click in Settings.
          </li>
        </ul>
        <p
          style={{
            fontSize: 13,
            lineHeight: 1.6,
            color: 'var(--text-decorative)',
            margin: '0 0 22px',
          }}
        >
          {/* These open a NESTED modal (z-index 260, above this dialog's 220),
              not a new tab and not a route change.

              A tab hand-off looks harmless but it breaks the consent moment:
              the reader lands in a separate context, has to find their way
              back, and on mobile the original tab is often lost entirely. This
              is the one place in the product where someone is being asked to
              agree to something before typing a confidential idea, so reading
              the document and returning to tick the box has to be a single
              uninterrupted motion. Dismissing the nested modal returns them
              here with their place, and any text they typed, intact. */}
          Read the full details on our{' '}
          <button type="button" className="cmodal__legal" onClick={() => setLegalDoc('confidentiality')}>
            Trust &amp; Security page
          </button>
          .{' '}
          <button type="button" className="cmodal__legal" onClick={() => setLegalDoc('terms')}>
            Terms of Service
          </button>{' '}
          and{' '}
          <button type="button" className="cmodal__legal" onClick={() => setLegalDoc('privacy')}>
            Privacy Policy
          </button>{' '}
          apply to your use of <Wordmark size="inherit" />.
        </p>
        {/* The "educational analysis, not professional advice" disclaimer used
            to sit here as a prominent italic line. Testers read it as a scam
            signal at the moment of first trust. It's been removed from this
            popup and lives where it belongs: the Terms of Service (linked just
            above), which already states EntreDash "is not legal advice, not a
            law firm" and to "verify anything you intend to act on with a
            qualified attorney." No legal protection is lost by relocating it. */}

        {error && (
          <div
            style={{
              fontSize: 13,
              color: 'var(--oxblood, #8B2E2E)',
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        {/* Pinned to the bottom of the scrolling card, so the buttons are always on screen on short phones. */}
        <div className="cmodal__actions">
          <Button onClick={handleAccept} loading={submitting} variant="brass">
            I understand, let&apos;s begin
          </Button>
          <Button
            onClick={onClose}
            variant="secondary"
            disabled={submitting}
          >
            Not yet
          </Button>
        </div>
      </div>

      {/* Nested legal reader. Sits above this dialog rather than replacing it,
          so closing it returns the user straight to the consent checkbox. */}
      <LegalModal doc={legalDoc} onClose={() => setLegalDoc(null)} />

      <style>{`
        @keyframes confidentialityDlg {
          from { opacity: 0; transform: translateY(8px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        /* Renders as a link but is a real button, because it opens a dialog
           rather than navigating. Inherits the surrounding paragraph's size and
           line-height so it sits inline in the sentence without disturbing it. */
.cmodal__actions {
          position: sticky; bottom: -28px; z-index: 1;
          display: flex; gap: 12px; flex-wrap: wrap;
          margin: 0 -32px -28px; padding: 16px 32px 28px;
          background: var(--surface-page);
          box-shadow: 0 -14px 18px -14px rgba(15, 23, 20, 0.18);
        }
        @media (max-width: 520px) {
          .cmodal__actions > * { flex: 1 1 100%; justify-content: center; }
        }
        .cmodal__legal {
          border: none;
          background: none;
          padding: 0;
          font: inherit;
          line-height: inherit;
          color: var(--brass-deep);
          text-decoration: underline;
          text-underline-offset: 2px;
          cursor: pointer;
        }
        .cmodal__legal:hover { text-decoration-thickness: 2px; }
      `}</style>
    </div>
  );
}
